import type { AnchorHTMLAttributes, ComponentChildren, ComponentType } from "preact";
import { createContext } from "preact";
import { useContext } from "preact/hooks";
import Router, { getCurrentUrl, type RoutableProps } from "preact-router";
import { hasSession } from "./auth-cache";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type RouteConfig = { page: AnyComponent; withLayout?: boolean };

type SpaRouterConfig = {
  routes: Record<string, RouteConfig>;
  Layout: ComponentType<{ children: ComponentChildren }>;
  unauthPaths?: string[];
  authPaths?: string[];
  base?: string;
  isAuthenticated?: () => boolean;
  unauthRedirect?: string | ((currentPath: string) => string);
  Loading?: ComponentType;
  NotFound?: ComponentType;
};

type AppProps = { url?: string };

type GateConfig = Pick<SpaRouterConfig, "unauthPaths" | "authPaths" | "isAuthenticated" | "unauthRedirect">;

const isGated = (appPath: (path: string) => string, { unauthPaths = [], authPaths }: GateConfig, currentPath: string): boolean =>
  authPaths ? authPaths.some((p) => currentPath === appPath(p)) : !unauthPaths.some((p) => currentPath === appPath(p));

const redirectUnauthenticated = (appPath: (path: string) => string, config: GateConfig): boolean => {
  const { isAuthenticated = hasSession, unauthRedirect = DEFAULT_UNAUTH_REDIRECT } = config;
  const currentPath = window.location.pathname;

  if (!isGated(appPath, config, currentPath) || isAuthenticated()) {
    return false;
  }

  window.location.href = typeof unauthRedirect === "function" ? unauthRedirect(currentPath + window.location.search) : unauthRedirect;
  return true;
};

const DEFAULT_APP_BASE = "/app";

const SpaUrlContext = createContext<string | undefined>(undefined);

export const useSpaUrl = (): string => useContext(SpaUrlContext) ?? getCurrentUrl();

const DEFAULT_UNAUTH_REDIRECT = "/";

const AppRoute = ({
  page: Page,
  withLayout = true,
  layout: Layout,
  ...routeParams
}: RoutableProps & {
  page: AnyComponent;
  withLayout?: boolean;
  layout: ComponentType<{ children: ComponentChildren }>;
}) =>
  withLayout ? (
    <Layout>
      <Page {...routeParams} />
    </Layout>
  ) : (
    <Page {...routeParams} />
  );
export const createSpaRouter = (
  config: SpaRouterConfig,
): {
  App: ComponentType<AppProps>;
  AppLink: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }>;
} => {
  const { routes, Layout, base = DEFAULT_APP_BASE, Loading, NotFound } = config;

  const appPath = (path: string) => base + (path === "/" ? "" : path) || "/";

  const AppLink = ({ href, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => {
    return <a {...rest} href={appPath(href)} />;
  };

  const App = ({ url }: AppProps) => {
    if (url === undefined && redirectUnauthenticated(appPath, config)) {
      return Loading ? <Loading /> : null;
    }

    return (
      <SpaUrlContext.Provider value={url}>
        <Router url={url}>
          {Object.entries(routes).map(([path, { page, withLayout }]) => (
            <AppRoute key={appPath(path)} path={appPath(path)} page={page} withLayout={withLayout} layout={Layout} />
          ))}
          {NotFound ? <AppRoute default page={NotFound} layout={Layout} /> : null}
        </Router>
      </SpaUrlContext.Provider>
    );
  };

  return { App, AppLink };
};
